/**
 * PODeliveryCalendar — Tab 3 on PurchaseOrdersPage
 * Data source: PO transactions (grouped by po_number).
 * Clicking a group opens ConfirmGroupDeliveryDialog.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, IconButton, Chip, Paper,
  CircularProgress, alpha, useTheme, Tooltip, Divider,
  Popover, List, ListItem, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, CalendarMonth as CalIcon,
  CheckCircle as CheckCircleIcon, LocalShipping as ShippingIcon,
  HourglassEmpty as PartialIcon, Close as CloseIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { fetchPOTransactions } from './poTransactionHelpers';
import ConfirmGroupDeliveryDialog from './ConfirmGroupDeliveryDialog';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July',
  'August','September','October','November','December'];

const STATUS_COLORS = {
  pending:   { bg: '#fff8e1', color: '#f57f17', border: '#ffca28' },
  partial:   { bg: '#e3f2fd', color: '#1565c0', border: '#42a5f5' },
  delivered: { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8' },
};

const STATUS_ICONS = {
  pending:   ShippingIcon,
  partial:   PartialIcon,
  delivered: CheckCircleIcon,
};

const TYPE_COLORS_VIEW = {
  products: '#1565c0', chemicals: '#7b1fa2', caps: '#00796b',
  bottles: '#2e7d32', labels: '#e65100', supplies: '#5d4037',
};

function getItemName(tx) {
  return tx.products?.name || tx.raw_materials?.name || tx.caps?.name ||
         tx.bottles?.name  || tx.labels?.name        || tx.supplies?.name || '—';
}
function getQty(tx) {
  if (tx._type === 'chemicals') return tx.unit_quantity ?? tx.total_quantity ?? 0;
  if (tx._type === 'products')  return tx.quantity_change ?? tx.total_base_units ?? 0;
  return tx.quantity ?? 0;
}

function POGroupViewDialog({ group, onClose, onConfirm }) {
  const theme = useTheme();
  if (!group) return null;
  const { po_number, transactions, status, expected_delivery_date, deliveryTiming, lastActualDate } = group;
  const sc   = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const hasPending = transactions.some(t => t.delivery_status !== 'delivered');
  const expectedStr = expected_delivery_date
    ? new Date(expected_delivery_date.split('T')[0] + 'T12:00:00')
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '—';
  const actualStr = lastActualDate
    ? new Date(lastActualDate + 'T12:00:00')
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        pb: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>PO #{po_number}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">Expected: {expectedStr}</Typography>
            {actualStr && (
              <Typography variant="body2" color="text.secondary">· Delivered: {actualStr}</Typography>
            )}
            <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, bgcolor: sc.bg, border: `1px solid ${sc.border}` }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: sc.color, textTransform: 'capitalize' }}>
                {status}
              </Typography>
            </Box>
            {deliveryTiming === 'early' && (
              <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, bgcolor: alpha('#2e7d32', 0.1), border: `1px solid ${alpha('#2e7d32', 0.35)}` }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#2e7d32' }}>Early</Typography>
              </Box>
            )}
            {deliveryTiming === 'late' && (
              <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, bgcolor: alpha('#c62828', 0.1), border: `1px solid ${alpha('#c62828', 0.35)}` }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#c62828' }}>Late</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {(() => {
            // Merge rows with same type+item — sum quantities, use worst status
            const merged = new Map();
            for (const tx of transactions) {
              const key = `${tx._type}::${getItemName(tx)}`;
              if (!merged.has(key)) {
                merged.set(key, { ...tx, _totalQty: parseFloat(getQty(tx)) || 0 });
              } else {
                const m = merged.get(key);
                m._totalQty += parseFloat(getQty(tx)) || 0;
                // if any lot is pending, mark group as pending
                if (tx.delivery_status !== 'delivered') m.delivery_status = tx.delivery_status;
              }
            }
            return [...merged.values()].map(tx => {
              const color     = TYPE_COLORS_VIEW[tx._type] || '#666';
              const delivered = tx.delivery_status === 'delivered';
              const typeLabel = tx._type ? tx._type.charAt(0).toUpperCase() + tx._type.slice(1) : tx._type;
              const qtyFormatted = tx._totalQty.toLocaleString();

              let txTiming = null;
              if (delivered && tx.actual_delivery_date && expected_delivery_date) {
                const txActual   = tx.actual_delivery_date.split('T')[0];
                const txExpected = expected_delivery_date.split('T')[0];
                if (txActual < txExpected) txTiming = 'early';
                else if (txActual > txExpected) txTiming = 'late';
              }

              return (
                <Box key={`${tx._type}-${getItemName(tx)}`} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2,
                  border: `1px solid ${alpha(color, 0.2)}`,
                  bgcolor: delivered ? alpha(theme.palette.success.main, 0.03) : 'white',
                }}>
                  <Chip label={typeLabel} size="small" sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 500, height: 20, fontSize: '0.68rem', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{getItemName(tx)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Qty: {qtyFormatted}</Typography>
                  {txTiming === 'early' && (
                    <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, flexShrink: 0, bgcolor: alpha('#2e7d32', 0.1), border: `1px solid ${alpha('#2e7d32', 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#2e7d32' }}>Early</Typography>
                    </Box>
                  )}
                  {txTiming === 'late' && (
                    <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, flexShrink: 0, bgcolor: alpha('#c62828', 0.1), border: `1px solid ${alpha('#c62828', 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#c62828' }}>Late</Typography>
                    </Box>
                  )}
                  <Box sx={{
                    px: 0.75, py: 0.15, borderRadius: 1, flexShrink: 0,
                    bgcolor: delivered ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                    border: `1px solid ${delivered ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.warning.main, 0.3)}`,
                  }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: delivered ? 'success.main' : 'warning.dark', textTransform: 'capitalize' }}>
                      {tx.delivery_status}
                    </Typography>
                  </Box>
                </Box>
              );
            });
          })()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.grey[200], 0.8)}` }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Close</Button>
        {hasPending && (
          <Button variant="contained" color="success" onClick={onConfirm} sx={{ borderRadius: 2, px: 3, fontWeight: 500 }}>
            Confirm Delivery
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function formatDateKey(d) {
  const parts = d.split('T')[0].split('-');
  return `${parts[0]}-${parts[1]}-${parts[2]}`;
}

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function firstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); }

export default function PODeliveryCalendar({ companyId }) {
  const theme = useTheme();
  const today = new Date();

  const [year,         setYear]         = useState(today.getFullYear());
  const [month,        setMonth]        = useState(today.getMonth());
  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filterFrom,   setFilterFrom]   = useState('');
  const [filterTo,     setFilterTo]     = useState('');
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [viewGroup,    setViewGroup]    = useState(null);

  // Day popover (multiple groups on same day)
  const [dayAnchor, setDayAnchor] = useState(null);
  const [dayGroups, setDayGroups] = useState([]);
  const [dayLabel,  setDayLabel]  = useState('');

  const openDayPopover = (e, groups, label) => {
    e.stopPropagation();
    setDayGroups(groups);
    setDayLabel(label);
    setDayAnchor(e.currentTarget);
  };
  const closeDayPopover = () => { setDayAnchor(null); setDayGroups([]); };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchPOTransactions(companyId);
        setTransactions(data);
      } catch (e) {
        console.error('PODeliveryCalendar fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId]);

  // Group transactions by po_number
  const groups = useMemo(() => {
    const map = {};
    transactions.forEach(tx => {
      if (!tx.po_number || !tx.expected_delivery_date) return;
      const key = tx.po_number;
      if (!map[key]) {
        map[key] = {
          po_number: key,
          transactions: [],
          expected_delivery_date: tx.expected_delivery_date,
        };
      }
      map[key].transactions.push(tx);
    });

    return Object.values(map)
      .map(group => {
        const allDelivered = group.transactions.every(t => t.delivery_status === 'delivered');
        const anyDelivered = group.transactions.some(t => t.delivery_status === 'delivered');
        const status = allDelivered ? 'delivered' : anyDelivered ? 'partial' : 'pending';

        // Compute delivery timing from actual vs expected date
        // Fall back to updated_at if actual_delivery_date is missing (some tables may not have it)
        const deliveredTxs = group.transactions.filter(t => t.delivery_status === 'delivered');
        const lastActual = deliveredTxs.length > 0
          ? deliveredTxs.map(t => ((t.actual_delivery_date || t.updated_at || t.created_at) || '').split('T')[0]).filter(Boolean).sort().pop()
          : null;
        const expectedDate = group.expected_delivery_date.split('T')[0];
        let deliveryTiming = null;
        if (lastActual) {
          if (lastActual < expectedDate) deliveryTiming = 'early';
          else if (lastActual > expectedDate) deliveryTiming = 'late';
          else deliveryTiming = 'on_time';
        }

        return { ...group, status, lastActualDate: lastActual, deliveryTiming };
      })
      .filter(group => {
        const d = group.expected_delivery_date.split('T')[0];
        if (filterFrom && d < filterFrom) return false;
        if (filterTo   && d > filterTo)   return false;
        return true;
      });
  }, [transactions, filterFrom, filterTo]);

  const deliveryMap = useMemo(() => {
    const map = {};
    groups.forEach(group => {
      const key = formatDateKey(group.expected_delivery_date);
      if (!map[key]) map[key] = [];
      map[key].push(group);
    });
    return map;
  }, [groups]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const cells = useMemo(() => {
    const first  = firstDayOfMonth(year, month);
    const total  = daysInMonth(year, month);
    const blanks = Array(first).fill(null);
    const days   = Array.from({ length: total }, (_, i) => i + 1);
    return [...blanks, ...days];
  }, [year, month]);

  const dayKey = (d) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const isToday = (d) =>
    d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const isPast = (d) =>
    new Date(year, month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const upcoming = useMemo(() => {
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return groups.filter(g => new Date(g.expected_delivery_date.split('T')[0] + 'T12:00:00') >= todayMidnight);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress size={48} /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      {/* ── Calendar (left) ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3,
          pb: 2.5, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={prevMonth} size="small" sx={{
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, bgcolor: 'white',
              '&:hover': { border: `1px solid ${theme.palette.primary.main}`, bgcolor: alpha(theme.palette.primary.main, 0.05) }
            }}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={nextMonth} size="small" sx={{
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, bgcolor: 'white',
              '&:hover': { border: `1px solid ${theme.palette.primary.main}`, bgcolor: alpha(theme.palette.primary.main, 0.05) }
            }}>
              <ChevronRight />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: '-0.3px', ml: 1 }}>
              {MONTHS[month]} <span style={{ color: theme.palette.primary.main }}>{year}</span>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={filterFrom ? dayjs(filterFrom) : null}
                onChange={v => setFilterFrom(v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', sx: { width: 155, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontWeight: 500, fontSize: '0.82rem' } } } }}
              />
              <DatePicker
                label="To"
                value={filterTo ? dayjs(filterTo) : null}
                onChange={v => setFilterTo(v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', sx: { width: 155, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontWeight: 500, fontSize: '0.82rem' } } } }}
              />
            </LocalizationProvider>
            {(filterFrom || filterTo) && (
              <Chip
                label="Clear"
                size="small"
                onClick={() => { setFilterFrom(''); setFilterTo(''); }}
                sx={{
                  fontWeight: 500, cursor: 'pointer', borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                  bgcolor: 'white', color: 'text.secondary',
                  '&:hover': { bgcolor: alpha(theme.palette.grey[200], 0.8) }
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {Object.values(deliveryMap).reduce((s, a) => s + a.length, 0)} deliveries this period
            </Typography>
            <Chip
              label="Today"
              size="small"
              onClick={goToday}
              sx={{
                fontWeight: 500, cursor: 'pointer', borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                color: 'primary.main',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) }
              }}
            />
          </Box>
        </Box>

        {/* Weekday labels */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1, px: 0.5 }}>
          {WEEKDAYS.map((w, i) => (
            <Typography key={w} variant="caption" sx={{
              textAlign: 'center', fontWeight: 500,
              color: i === 0 || i === 6 ? alpha(theme.palette.primary.main, 0.6) : 'text.secondary',
              py: 1, fontSize: '0.78rem', letterSpacing: '0.5px', textTransform: 'uppercase'
            }}>
              {w}
            </Typography>
          ))}
        </Box>

        {/* Day grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {cells.map((d, idx) => {
            if (!d) return <Box key={`empty-${idx}`} />;
            const key    = dayKey(d);
            const events = deliveryMap[key] || [];
            const todayCell = isToday(d);
            const pastCell  = isPast(d);

            return (
              <Box
                key={key}
                onClick={events.length > 1 ? (e) => openDayPopover(e, events, `${MONTHS[month]} ${d}, ${year}`) : undefined}
                sx={{
                  minHeight: 100, borderRadius: 2, p: 1, overflow: 'hidden',
                  border: `1px solid ${todayCell ? theme.palette.primary.main : alpha(theme.palette.grey[300], 0.8)}`,
                  bgcolor: todayCell
                    ? alpha(theme.palette.primary.main, 0.04)
                    : events.length > 0 ? 'white' : pastCell ? alpha(theme.palette.grey[50], 0.5) : 'white',
                  transition: 'all 0.15s ease',
                  cursor: events.length > 1 ? 'pointer' : 'default',
                  '&:hover': events.length > 0 ? {
                    borderColor: theme.palette.primary.light,
                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
                    transform: 'translateY(-1px)'
                  } : {}
                }}
              >
                {/* Day number */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                  <Box sx={{
                    width: 26, height: 26, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: todayCell ? theme.palette.primary.main : 'transparent',
                  }}>
                    <Typography variant="caption" sx={{
                      fontWeight: todayCell ? 500 : pastCell ? 400 : 500,
                      color: todayCell ? 'white' : pastCell ? alpha(theme.palette.text.primary, 0.4) : 'text.primary',
                      fontSize: '0.82rem', lineHeight: 1
                    }}>
                      {d}
                    </Typography>
                  </Box>
                </Box>

                {/* Event pills */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  {events.slice(0, 3).map(group => {
                    const sc = STATUS_COLORS[group.status] || STATUS_COLORS.pending;
                    return (
                      <Tooltip
                        key={group.po_number}
                        title={`${group.po_number} — ${group.transactions.length} item${group.transactions.length !== 1 ? 's' : ''} (${group.status})`}
                        arrow
                      >
                        <Box
                          onClick={(e) => { e.stopPropagation(); setViewGroup(group); }}
                          sx={{
                            px: 0.75, py: 0.25, borderRadius: 1,
                            bgcolor: sc.bg, border: `1px solid ${sc.border}`,
                            overflow: 'hidden', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            '&:hover': { filter: 'brightness(0.95)' }
                          }}
                        >
                          <Typography variant="caption" noWrap sx={{
                            fontSize: '0.68rem', fontWeight: 500,
                            color: sc.color, lineHeight: 1.4, flex: 1
                          }}>
                            {group.po_number}
                          </Typography>
                          {group.deliveryTiming === 'early' && (
                            <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: '#2e7d32', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'white', lineHeight: 1.4 }}>EARLY</Typography>
                            </Box>
                          )}
                          {group.deliveryTiming === 'late' && (
                            <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: '#c62828', flexShrink: 0 }}>
                              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'white', lineHeight: 1.4 }}>LATE</Typography>
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {events.length > 3 && (
                    <Box
                      onClick={(e) => openDayPopover(e, events, `${MONTHS[month]} ${d}, ${year}`)}
                      sx={{
                        px: 0.75, py: 0.2, borderRadius: 1, cursor: 'pointer',
                        bgcolor: alpha(theme.palette.grey[400], 0.15),
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1), borderColor: theme.palette.primary.light }
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 500 }}>
                        +{events.length - 3} more
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Legend */}
        <Box sx={{ mt: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Status:</Typography>
          {Object.entries(STATUS_COLORS).map(([status, sc]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: sc.bg, border: `1px solid ${sc.border}` }} />
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{status}</Typography>
            </Box>
          ))}
          <Box sx={{ width: 1, height: 14, bgcolor: 'divider', mx: 0.5 }} />
          {(() => {
            const earlyCount = groups.filter(g => g.deliveryTiming === 'early').length;
            const lateCount  = groups.filter(g => g.deliveryTiming === 'late').length;
            return (<>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: '#2e7d32' }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'white', lineHeight: 1.4 }}>EARLY</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Early delivery{earlyCount > 0 ? ` (${earlyCount})` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: '#c62828' }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'white', lineHeight: 1.4 }}>LATE</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Late delivery{lateCount > 0 ? ` (${lateCount})` : ''}
                </Typography>
              </Box>
            </>);
          })()}
        </Box>
      </Box>

      {/* ── Day popover (multiple groups on same day) ─────────────────────── */}
      <Popover
        open={Boolean(dayAnchor)}
        anchorEl={dayAnchor}
        onClose={closeDayPopover}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 260, maxWidth: 340, boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`, border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}` } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1.5, color: 'text.primary' }}>
            {dayLabel}
          </Typography>
          <List dense disablePadding>
            {dayGroups.map((group, i) => {
              const sc = STATUS_COLORS[group.status] || STATUS_COLORS.pending;
              const Icon = STATUS_ICONS[group.status] || ShippingIcon;
              return (
                <Box key={group.po_number}>
                  <ListItem
                    disableGutters
                    sx={{ cursor: 'pointer', borderRadius: 1, px: 1, py: 0.75, '&:hover': { bgcolor: alpha(theme.palette.grey[100], 0.8) } }}
                    onClick={() => { closeDayPopover(); setViewGroup(group); }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Icon sx={{ fontSize: 14, color: sc.color, flexShrink: 0 }} />
                      <ListItemText
                        primary={group.po_number}
                        secondary={`${group.transactions.length} item${group.transactions.length !== 1 ? 's' : ''}`}
                        primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: '0.85rem' } }}
                        secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                      />
                      <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, bgcolor: sc.bg, border: `1px solid ${sc.border}`, flexShrink: 0 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: sc.color, textTransform: 'capitalize' }}>
                          {group.status}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                  {i < dayGroups.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
                </Box>
              );
            })}
          </List>
        </Box>
      </Popover>

      {/* ── Upcoming sidebar (right) ─────────────────────────────────────── */}
      <Box sx={{ width: 300, flexShrink: 0 }}>
        <Paper sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden'
        }}>
          <Box sx={{
            px: 2.5, py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'white'
          }}>
            <CalIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Upcoming Deliveries
            </Typography>
            {upcoming.length > 0 && (
              <Chip label={upcoming.length} size="small" color="primary" sx={{ ml: 'auto', height: 20, fontSize: '0.72rem', fontWeight: 500 }} />
            )}
          </Box>

          <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {upcoming.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  No upcoming deliveries
                </Typography>
              </Box>
            ) : (
              upcoming.slice(0, 12).map((group, i) => {
                const sc   = STATUS_COLORS[group.status] || STATUS_COLORS.pending;
                const date = new Date(group.expected_delivery_date.split('T')[0] + 'T12:00:00');
                const isDelivToday = date.toDateString() === today.toDateString();
                return (
                  <Box key={group.po_number}>
                    <Box
                      onClick={() => setViewGroup(group)}
                      sx={{
                        px: 2.5, py: 1.75, cursor: 'pointer',
                        bgcolor: isDelivToday ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                        '&:hover': { bgcolor: alpha(theme.palette.grey[200], 0.6) },
                        transition: 'background 0.15s'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="caption" sx={{
                          fontWeight: 500,
                          color: isDelivToday ? 'primary.main' : 'text.secondary',
                          fontSize: '0.75rem'
                        }}>
                          {isDelivToday ? 'Today' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                        <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, bgcolor: sc.bg, border: `1px solid ${sc.border}` }}>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: sc.color, textTransform: 'capitalize' }}>
                            {group.status}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.85rem' }}>
                        {group.po_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        {group.transactions.length} item{group.transactions.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {i < upcoming.slice(0, 12).length - 1 && (
                      <Divider sx={{ mx: 2.5, opacity: 0.5 }} />
                    )}
                  </Box>
                );
              })
            )}
            {upcoming.length > 12 && (
              <Box sx={{ px: 2.5, py: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  +{upcoming.length - 12} more upcoming
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ── View Group Dialog (read-only) ─────────────────────────────────── */}
      {viewGroup && !confirmGroup && (
        <POGroupViewDialog
          group={viewGroup}
          onClose={() => setViewGroup(null)}
          onConfirm={() => { setConfirmGroup(viewGroup.transactions); setViewGroup(null); }}
        />
      )}

      {/* ── Confirm Group Delivery Dialog ────────────────────────────────── */}
      {confirmGroup && (
        <ConfirmGroupDeliveryDialog
          open={!!confirmGroup}
          onClose={() => setConfirmGroup(null)}
          transactions={confirmGroup}
          companyId={companyId}
          onSuccess={() => {
            setConfirmGroup(null);
            fetchPOTransactions(companyId).then(data => setTransactions(data));
          }}
        />
      )}
    </Box>
  );
}
